import { useMemo, useCallback } from 'react';
import { useLingui } from '@lingui/react';
import { format, startOfWeek, addDays, isSameDay, isToday, startOfDay, parseISO } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { cn } from '@/lib/utils';
import { GripVertical, Repeat } from 'lucide-react';
import type { Shift, CalendarViewMode } from '@/types/shifts';
import { formatWeekday } from '@/lib/format';
import type { ViewMode } from '@/utils/shiftHelpers';
import { getEmployeeInitials } from '@/utils/shiftHelpers';
import type { EmployeeAbsence } from '@/types/employeeAbsences';
// `CoverageResponse` is the legacy per-day shape the hook still builds for this
// grid; it lives with the hook, not in `@/types/coverage` (which now only
// carries the unified range response). See useShiftCoverage.ts.
import type { CoverageResponse } from '@/hooks/useShiftCoverage';
import type { ZeitkontoProjection } from '@/types/zeitkonto';
import type { SlotCoverage } from '@/types/coverage';
import { AbsenceCard, AbsenceBar } from './AbsenceBlock';
import { GroupCoverageBadge } from './GroupCoverageBadge';
import { ShiftLegend, ShiftLifecycleGlyphs, shiftBlockClasses, shiftLifecycleTitle } from './shiftLifecycle';
import { OPEN_SHIFT_BLOCK_CLASSES, ShiftRequestGlyphs, isOpenShift, useOpenShiftLabel } from './openShifts';
import { ShiftDndProvider, ShiftDropCell, useShiftDraggable, type ShiftDropTarget } from './shiftDragDrop';
import { EmployeeZeitkontoBadge } from './EmployeeZeitkontoBadge';
import { GroupStaffingStrip, StaffingLegend } from './GroupStaffingStrip';
import { SurplusRunBadge } from './SurplusRunBadge';
import { ArrivalsPopover } from './ArrivalsPopover';
import {
  CombinationBlock,
  CombinationBlockLabel,
  CombinationPreviewLabel,
  type CombinationBlockPosition,
} from './CombinationBlock';
import { CombinationChip } from './CombinationChip';
import {
  AvailabilityHatch,
  AvailabilityLegend,
  AvailabilityStrip,
  BlockedDayLock,
  availabilityForDay,
} from './AvailabilityMarks';
import type { AvailabilityRange } from '@/types/availability';
import {
  activeCombinationsOn,
  combinationName,
  combinationRowSpan,
  combinationsForGroupOn,
  isoWeekday,
  orderResourcesForCombinations,
  timeWindowPercent,
  type CombinationGroups,
} from './combinationLayout';
import type { CombinationSuggestion, GroupCombination, UnitCoverage } from '@/types/groupCombinations';

interface EmployeeData {
  id?: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
}

interface GroupData {
  id?: string;
  name?: string;
  avatarUrl?: string;
  color?: string;
  childcare_id?: string;
  sort_order?: number;
}

interface ShiftCalendarGridProps {
  shifts: Shift[];
  absences?: EmployeeAbsence[];
  viewMode: ViewMode;
  calendarView: CalendarViewMode;
  currentDate: Date;
  employeeMap: Map<string, EmployeeData>;
  groupMap: Map<string, GroupData>;
  coverageData?: CoverageResponse;
  /** Per-15-minute slot coverage for the day (byGroup day view only) */
  slotCoverage?: SlotCoverage;
  /** Per-employee Zeitkonto projection, keyed by employee_id (byEmployee view only) */
  zeitkontoByEmployee?: Record<string, ZeitkontoProjection>;
  onShiftClick: (shift: Shift) => void;
  onCellClick: (date: Date, resourceId: string) => void;
  onAbsenceClick?: (absence: EmployeeAbsence) => void;
  filterChildcareId?: string | null;
  filterGroupId?: string | null;
  /** Group combinations of the visible range (byGroup views only) */
  combinations?: GroupCombination[];
  /** Opens a combination for editing. Absent when the user cannot edit. */
  onCombinationClick?: (combination: GroupCombination) => void;
  /** A suggestion previewed as a dashed block in the By Group day view (#1579 part 4) */
  previewSuggestion?: CombinationSuggestion | null;
  /**
   * Drag and drop (#2038): called when a shift is dropped on a cell of the week view, or
   * on an employee row of the By Employee day view. Absent: no drag.
   */
  onShiftDrop?: (shift: Shift, target: ShiftDropTarget) => void;
  /** Group-lead write scope (#2043): blocks for which this returns false do not drag. */
  canDragShift?: (shift: Shift) => boolean;
  /**
   * Employee availability of the visible range (#2041, By Employee views only): unavailable
   * hours are hatched, blocked days carry a lock.
   */
  availability?: AvailabilityRange;
}

// Day view hours (6:00 to 22:00)
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;

// By Group day rows have a fixed height, so a combination block can span several rows.
export const GROUP_DAY_ROW_HEIGHT = 76;
const GROUP_DAY_ROW_GAP = 8;
// The resource name column (220px) plus the gap before the timeline (gap-2).
const DAY_TIMELINE_OFFSET = 228;

const getShortDayMonthFormat = (locale: string): string => {
  switch (locale) {
    case 'de': return 'd.M.';
    default: return 'd/M';
  }
};

/**
 * Avatar component for employees with image URL resolution
 */
function EmployeeAvatar({ employee }: { employee: EmployeeData }) {
  const avatarUrlRaw = employee.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;
  const needsImageResolution = isImageId(avatarUrl);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  }

  const initials = getEmployeeInitials(employee.firstname, employee.lastname);

  return (
    <Avatar className="h-8 w-8 flex-shrink-0">
      {finalAvatarUrl && (
        <AvatarImage
          src={finalAvatarUrl}
          alt={`${employee.firstname} ${employee.lastname}`}
        />
      )}
      <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Avatar component for groups with image URL resolution
 * Uses first 2 letters of group name for fallback (aligned with capacity overview)
 */
function GroupAvatar({ group }: { group: GroupData }) {
  const avatarUrlRaw = group.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;
  const needsImageResolution = isImageId(avatarUrl);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  }

  // Always use first two letters of the group name (aligned with capacity overview)
  const groupInitials = group.name ? group.name.substring(0, 2).toUpperCase() : '??';

  return (
    <Avatar
      className="h-8 w-8 flex-shrink-0"
      style={{
        backgroundColor: group.color ? `${group.color}20` : undefined,
        borderColor: group.color || undefined,
        borderWidth: group.color ? '1px' : undefined,
      }}
    >
      {finalAvatarUrl && (
        <AvatarImage src={finalAvatarUrl} alt={group.name || 'Group'} />
      )}
      <AvatarFallback
        className="text-xs font-medium"
        style={{
          backgroundColor: group.color ? `${group.color}20` : undefined,
          color: group.color || undefined,
        }}
      >
        {groupInitials}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Shift card displayed in week view grid cell
 * - In byGroup mode: shows employee name, then times
 * - In byEmployee mode: shows group names, then times
 */
function ShiftCard({
  shift,
  onClick,
  viewMode,
}: {
  shift: Shift;
  onClick: () => void;
  viewMode: ViewMode;
}) {
  // API uses snake_case: start_time, end_time (HH:mm format strings)
  const startTime = shift.start_time || '';
  const endTime = shift.end_time || '';

  const openLabel = useOpenShiftLabel(shift);

  // Get display name based on view mode
  const getDisplayName = () => {
    if (viewMode === 'byGroup') {
      // An open shift (#2040) says so, with its claim count.
      if (openLabel) return openLabel;
      // In group view, show employee name
      if (shift.employee) {
        const firstName = shift.employee.firstname || '';
        const lastName = shift.employee.lastname || '';
        return `${firstName} ${lastName}`.trim() || undefined;
      }
      return undefined;
    } else {
      // In employee view, show group names
      if (shift.groups && shift.groups.length > 0) {
        return shift.groups.map((g) => g.name).join(', ');
      }
      return undefined;
    }
  };

  const displayName = getDisplayName();
  const { i18n } = useLingui();
  const drag = useShiftDraggable(shift);

  return (
    <button
      type="button"
      ref={drag.setNodeRef}
      {...drag.listeners}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={[openLabel, shiftLifecycleTitle(shift, i18n)].filter(Boolean).join('\n') || undefined}
      data-testid={`shift-block-${shift.id}`}
      data-status={shift.status}
      className={cn(
        'w-full text-left p-2 rounded-md border text-xs transition-colors',
        'hover:shadow-md cursor-pointer',
        drag.enabled && 'touch-none',
        drag.isDragging && 'opacity-40',
        shiftBlockClasses(shift.status),
        isOpenShift(shift) && OPEN_SHIFT_BLOCK_CLASSES
      )}
    >
      {displayName && (
        <div className="flex items-center justify-between gap-1">
          <span className="flex min-w-0 items-center gap-1">
            {drag.enabled && <DragGrip />}
            <span className="font-medium truncate">{displayName}</span>
          </span>
          <span className="inline-flex flex-shrink-0 items-center gap-0.5">
            <ShiftRequestGlyphs shift={shift} />
            <ShiftLifecycleGlyphs shift={shift} />
            {shift.pattern_id && (
              <Repeat className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
            )}
          </span>
        </div>
      )}
      <div className={cn('truncate', displayName ? 'text-muted-foreground' : 'font-medium', !displayName && 'flex items-center justify-between gap-1')}>
        <span className="truncate">{startTime} - {endTime}</span>
        {!displayName && (
          <span className="inline-flex flex-shrink-0 items-center gap-0.5">
            <ShiftRequestGlyphs shift={shift} />
            <ShiftLifecycleGlyphs shift={shift} />
            {shift.pattern_id && <Repeat className="h-3 w-3 flex-shrink-0" />}
          </span>
        )}
      </div>
    </button>
  );
}

/** The drag handle glyph of a block; the whole block drags, the glyph names it. */
function DragGrip() {
  const { i18n } = useLingui();
  return (
    <GripVertical
      className="h-3 w-3 flex-shrink-0 opacity-60 print:hidden"
      role="img"
      aria-label={i18n._({ id: 'shifts.dnd.handle', message: 'Move shift (drag)' })}
      data-testid="shift-drag-handle"
    />
  );
}

/**
 * Shift bar displayed in day view timeline
 * - In byGroup mode: shows employee name, then times
 * - In byEmployee mode: shows group names, then times
 */
function ShiftBar({
  shift,
  onClick,
  startHour,
  endHour,
  totalHours,
  viewMode,
}: {
  shift: Shift;
  onClick: () => void;
  startHour: number;
  endHour: number;
  totalHours: number;
  viewMode: ViewMode;
}) {
  // Calculate position and width as percentages
  const startPercent = ((startHour - DAY_START_HOUR) / totalHours) * 100;
  const widthPercent = ((endHour - startHour) / totalHours) * 100;

  // API uses snake_case: start_time, end_time (HH:mm format strings)
  const startTimeStr = shift.start_time || '';
  const endTimeStr = shift.end_time || '';

  const openLabel = useOpenShiftLabel(shift);

  // Get display name based on view mode (same logic as ShiftCard)
  const getDisplayName = () => {
    if (viewMode === 'byGroup') {
      if (openLabel) return openLabel;
      // In group view, show employee name
      if (shift.employee) {
        const firstName = shift.employee.firstname || '';
        const lastName = shift.employee.lastname || '';
        return `${firstName} ${lastName}`.trim() || undefined;
      }
      return undefined;
    } else {
      // In employee view, show group names
      if (shift.groups && shift.groups.length > 0) {
        return shift.groups.map((g) => g.name).join(', ');
      }
      return undefined;
    }
  };

  const displayName = getDisplayName();
  const { i18n } = useLingui();
  const drag = useShiftDraggable(shift);

  return (
    <button
      type="button"
      ref={drag.setNodeRef}
      {...drag.listeners}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={[openLabel, shiftLifecycleTitle(shift, i18n)].filter(Boolean).join('\n') || undefined}
      data-testid={`shift-block-${shift.id}`}
      data-status={shift.status}
      className={cn(
        'absolute h-10 top-1/2 -translate-y-1/2 rounded-md border text-xs transition-colors',
        'hover:shadow-md cursor-pointer flex items-center gap-2 px-2 overflow-hidden',
        drag.enabled && 'touch-none',
        drag.isDragging && 'opacity-40',
        shiftBlockClasses(shift.status),
        isOpenShift(shift) && OPEN_SHIFT_BLOCK_CLASSES
      )}
      style={{
        left: `${startPercent}%`,
        width: `${widthPercent}%`,
        minWidth: '40px',
      }}
    >
      {drag.enabled && <DragGrip />}
      {displayName && (
        <span className="truncate font-medium">{displayName}</span>
      )}
      <span className={cn('truncate', displayName ? 'text-muted-foreground' : 'font-medium')}>
        {startTimeStr} - {endTimeStr}
      </span>
      <ShiftRequestGlyphs shift={shift} />
      <ShiftLifecycleGlyphs shift={shift} />
      {shift.pattern_id && (
        <Repeat className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
      )}
    </button>
  );
}

/**
 * Grid-based shift calendar matching the Capacity Overview layout
 */
export function ShiftCalendarGrid({
  shifts,
  absences = [],
  viewMode,
  calendarView,
  currentDate,
  employeeMap,
  groupMap,
  coverageData,
  zeitkontoByEmployee,
  onShiftClick,
  onCellClick,
  onAbsenceClick,
  filterChildcareId,
  filterGroupId,
  slotCoverage,
  combinations,
  onCombinationClick,
  previewSuggestion,
  onShiftDrop,
  canDragShift,
  availability,
}: ShiftCalendarGridProps) {
  const { i18n } = useLingui();

  // Calculate the days to display (for week view)
  const days = useMemo(() => {
    if (calendarView === 'day') {
      return [currentDate];
    }
    // Week view: Monday to Sunday
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [currentDate, calendarView]);

  // Calculate hours for day view
  const hours = useMemo(() => {
    return Array.from(
      { length: DAY_END_HOUR - DAY_START_HOUR },
      (_, i) => DAY_START_HOUR + i
    );
  }, []);

  // Helper function to check if a group passes the filters
  const groupPassesFilter = useCallback((groupId: string): boolean => {
    // If a specific group is selected, only that group passes
    if (filterGroupId) {
      return groupId === filterGroupId;
    }
    // If a childcare is selected, check if the group belongs to it
    if (filterChildcareId) {
      const groupData = groupMap.get(groupId);
      return groupData?.childcare_id === filterChildcareId;
    }
    // No filters active, all groups pass
    return true;
  }, [filterGroupId, filterChildcareId, groupMap]);

  // Get unique resources (employees or groups) from shifts
  // A previewed suggestion shows in the By Group day view on its weekdays only.
  const previewActive =
    !!previewSuggestion &&
    viewMode === 'byGroup' &&
    calendarView === 'day' &&
    previewSuggestion.weekdays.includes(isoWeekday(currentDate));

  const resources = useMemo(() => {
    const resourceSet = new Map<
      string,
      { id: string; name: string; data: EmployeeData | GroupData }
    >();

    if (viewMode === 'byEmployee') {
      // Get all employees from shifts (API uses snake_case: employee_id)
      shifts.forEach((shift) => {
        if (shift.employee_id && !resourceSet.has(shift.employee_id)) {
          const employee = employeeMap.get(shift.employee_id);
          resourceSet.set(shift.employee_id, {
            id: shift.employee_id,
            name: employee
              ? `${employee.firstname || ''} ${employee.lastname || ''}`.trim()
              : 'Unknown',
            data: employee || { id: shift.employee_id },
          });
        }
      });
      // Also add employees without shifts from the map
      employeeMap.forEach((emp, id) => {
        if (!resourceSet.has(id)) {
          resourceSet.set(id, {
            id,
            name: `${emp.firstname || ''} ${emp.lastname || ''}`.trim(),
            data: emp,
          });
        }
      });
    } else {
      // Get all groups from shifts (API uses groups array of GroupBrief objects)
      shifts.forEach((shift) => {
        shift.groups?.forEach((group) => {
          const groupId = group.id;
          if (groupId && !resourceSet.has(groupId)) {
            // Apply filter: skip groups that don't pass the filter
            if (!groupPassesFilter(groupId)) return;

            const groupData = groupMap.get(groupId);
            resourceSet.set(groupId, {
              id: groupId,
              name: groupData?.name || group.name || 'Unknown',
              data: groupData || { id: groupId, name: group.name },
            });
          }
        });
      });
      // Also add groups without shifts from the map (if they pass the filter)
      groupMap.forEach((grp, id) => {
        if (!resourceSet.has(id) && groupPassesFilter(id)) {
          resourceSet.set(id, {
            id,
            name: grp.name || 'Unknown',
            data: grp,
          });
        }
      });
    }

    // Add "Unassigned" row for shifts without assignment (only if no filters active).
    // By Employee, a shift without an employee is an open shift (#2040): its row is named
    // so and shows with every filter, because the group filter already narrowed the shifts.
    if (viewMode === 'byEmployee' || (!filterGroupId && !filterChildcareId)) {
      const hasUnassigned = shifts.some((s) =>
        viewMode === 'byEmployee' ? !s.employee_id : !s.groups?.length
      );
      if (hasUnassigned) {
        resourceSet.set('unassigned', {
          id: 'unassigned',
          name:
            viewMode === 'byEmployee'
              ? i18n._({ id: 'shifts.open.row', message: 'Open shifts' })
              : i18n._({ id: 'shifts.unassigned', message: 'Unassigned' }),
          data: {},
        });
      }
    }

    const sorted = Array.from(resourceSet.values()).sort((a, b) => {
      if (a.id === 'unassigned') return 1;
      if (b.id === 'unassigned') return -1;
      // Only group rows carry a sort order; employee rows fall back to the
      // name comparison below, exactly as before.
      const sortOrderOf = (data?: EmployeeData | GroupData) =>
        data && 'sort_order' in data ? data.sort_order ?? 999 : 999;
      const aOrder = sortOrderOf(a.data);
      const bOrder = sortOrderOf(b.data);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });

    // In the By Group day view the groups of each combination, and of a previewed
    // suggestion, sit next to each other.
    if (viewMode === 'byGroup' && calendarView === 'day') {
      const units: CombinationGroups[] = activeCombinationsOn(combinations ?? [], currentDate);
      if (previewActive && previewSuggestion) units.push(previewSuggestion);
      if (units.length > 0) return orderResourcesForCombinations(sorted, units);
    }
    return sorted;
  }, [shifts, viewMode, employeeMap, groupMap, i18n, groupPassesFilter, filterGroupId, filterChildcareId, calendarView, combinations, currentDate, previewActive, previewSuggestion]);

  // Combination blocks of the By Group day view, positioned over the rows of each unit.
  const unitBlocks = useMemo(() => {
    if (viewMode !== 'byGroup' || calendarView !== 'day' || !slotCoverage) return [];
    const resourceIds = resources.map((r) => r.id);
    const rowPitch = GROUP_DAY_ROW_HEIGHT + GROUP_DAY_ROW_GAP;
    return slotCoverage.units.flatMap((unit: UnitCoverage) => {
      const span = combinationRowSpan(resourceIds, unit);
      const window = timeWindowPercent(unit.start, unit.end, DAY_START_HOUR, DAY_END_HOUR);
      if (!span || !window) return [];
      const combination = combinations?.find((c) => c.id === unit.combination_id);
      const position: CombinationBlockPosition = {
        top: span.firstRow * rowPitch,
        height: span.rowCount * rowPitch - GROUP_DAY_ROW_GAP,
        leftPercent: window.leftPercent,
        widthPercent: window.widthPercent,
        timelineOffset: DAY_TIMELINE_OFFSET,
      };
      return [{
        unit,
        combination,
        position,
        hostName: groupMap.get(unit.host_group_id)?.name ?? combination?.host_group_name ?? '',
        memberNames: unit.member_group_ids.map((id) => groupMap.get(id)?.name ?? ''),
      }];
    });
  }, [viewMode, calendarView, slotCoverage, resources, combinations, groupMap]);

  // The previewed suggestion, positioned like a combination block.
  const previewBlock = useMemo(() => {
    if (!previewActive || !previewSuggestion) return null;
    const span = combinationRowSpan(resources.map((r) => r.id), previewSuggestion);
    const window = timeWindowPercent(previewSuggestion.start, previewSuggestion.end, DAY_START_HOUR, DAY_END_HOUR);
    if (!span || !window) return null;
    const rowPitch = GROUP_DAY_ROW_HEIGHT + GROUP_DAY_ROW_GAP;
    const position: CombinationBlockPosition = {
      top: span.firstRow * rowPitch,
      height: span.rowCount * rowPitch - GROUP_DAY_ROW_GAP,
      leftPercent: window.leftPercent,
      widthPercent: window.widthPercent,
      timelineOffset: DAY_TIMELINE_OFFSET,
    };
    return {
      position,
      name: combinationName(previewSuggestion.host_group_name, previewSuggestion.member_group_names),
      staffSaved: previewSuggestion.staff_saved,
    };
  }, [previewActive, previewSuggestion, resources]);

  // Get shifts for a specific cell (resource + day) - for week view
  const getShiftsForCell = (resourceId: string, date: Date): Shift[] => {
    return shifts.filter((shift) => {
      // Check if shift is on this day (API uses shift_date)
      const shiftDate = shift.shift_date ? parseISO(shift.shift_date) : null;
      if (!shiftDate || !isSameDay(shiftDate, date)) return false;

      // Check if shift belongs to this resource
      if (viewMode === 'byEmployee') {
        if (resourceId === 'unassigned') {
          return !shift.employee_id;
        }
        return shift.employee_id === resourceId;
      } else {
        if (resourceId === 'unassigned') {
          return !shift.groups?.length;
        }
        return shift.groups?.some((g) => g.id === resourceId);
      }
    });
  };

  // Get shifts for a resource on the current day - for day view
  const getShiftsForResource = (resourceId: string): Shift[] => {
    return shifts.filter((shift) => {
      // Check if shift is on current day (API uses shift_date)
      const shiftDate = shift.shift_date ? parseISO(shift.shift_date) : null;
      if (!shiftDate || !isSameDay(shiftDate, currentDate)) return false;

      // Check if shift belongs to this resource
      if (viewMode === 'byEmployee') {
        if (resourceId === 'unassigned') {
          return !shift.employee_id;
        }
        return shift.employee_id === resourceId;
      } else {
        if (resourceId === 'unassigned') {
          return !shift.groups?.length;
        }
        return shift.groups?.some((g) => g.id === resourceId);
      }
    });
  };

  // Get absences for a specific cell (resource + day) - for week view
  // In byEmployee mode: filter absences by employeeId directly
  // In byGroup mode: find employees with shifts in this group on this date, show their absences
  const getAbsencesForCell = (resourceId: string, date: Date): EmployeeAbsence[] => {
    if (resourceId === 'unassigned') return [];

    // Normalize calendar date to local start-of-day for comparison
    const cellDay = startOfDay(date);

    // Determine which employee IDs to match
    let employeeIds: Set<string>;
    if (viewMode === 'byEmployee') {
      employeeIds = new Set([resourceId]);
    } else {
      // Group view: collect employee_ids from shifts in this group on this date
      employeeIds = new Set<string>();
      for (const shift of shifts) {
        const shiftDate = shift.shift_date ? parseISO(shift.shift_date) : null;
        if (!shiftDate || !isSameDay(shiftDate, date)) continue;
        if (!shift.groups?.some((g) => g.id === resourceId)) continue;
        if (shift.employee_id) employeeIds.add(shift.employee_id);
      }
      if (employeeIds.size === 0) return [];
    }

    return absences.filter((absence) => {
      // Check employee matches one of the target IDs
      if (!absence.employee_id || !employeeIds.has(absence.employee_id)) return false;

      // Parse ISO dates and normalize to local start-of-day
      // ISO dates like "2025-01-15" are date-only and should be compared as local dates
      if (!absence.from_date || !absence.to_date) return false;
      const [fromYear, fromMonth, fromDay] = absence.from_date.split('-').map(Number);
      const [toYear, toMonth, toDay] = absence.to_date.split('-').map(Number);
      const fromDate = startOfDay(new Date(fromYear, fromMonth - 1, fromDay));
      const toDate = startOfDay(new Date(toYear, toMonth - 1, toDay));

      // Date is within absence range (inclusive)
      return cellDay >= fromDate && cellDay <= toDate;
    });
  };

  // Get absences for a resource on the current day - for day view
  // In byEmployee mode: filter absences by employeeId directly
  // In byGroup mode: find employees with shifts in this group today, show their absences
  const getAbsencesForResource = (resourceId: string): EmployeeAbsence[] => {
    if (resourceId === 'unassigned') return [];

    // Normalize current date to local start-of-day for comparison
    const currentDay = startOfDay(currentDate);

    // Determine which employee IDs to match
    let employeeIds: Set<string>;
    if (viewMode === 'byEmployee') {
      employeeIds = new Set([resourceId]);
    } else {
      // Group view: collect employee_ids from shifts in this group today
      employeeIds = new Set<string>();
      for (const shift of shifts) {
        const shiftDate = shift.shift_date ? parseISO(shift.shift_date) : null;
        if (!shiftDate || !isSameDay(shiftDate, currentDate)) continue;
        if (!shift.groups?.some((g) => g.id === resourceId)) continue;
        if (shift.employee_id) employeeIds.add(shift.employee_id);
      }
      if (employeeIds.size === 0) return [];
    }

    return absences.filter((absence) => {
      // Check employee matches one of the target IDs
      if (!absence.employee_id || !employeeIds.has(absence.employee_id)) return false;

      // Parse ISO dates and normalize to local start-of-day
      // ISO dates like "2025-01-15" are date-only and should be compared as local dates
      if (!absence.from_date || !absence.to_date) return false;
      const [fromYear, fromMonth, fromDay] = absence.from_date.split('-').map(Number);
      const [toYear, toMonth, toDay] = absence.to_date.split('-').map(Number);
      const fromDate = startOfDay(new Date(fromYear, fromMonth - 1, fromDay));
      const toDate = startOfDay(new Date(toYear, toMonth - 1, toDay));

      return currentDay >= fromDate && currentDay <= toDate;
    });
  };

  // Parse shift time to get hour as number (API uses HH:mm format strings)
  const getShiftHours = (
    shift: Shift
  ): { startHour: number; endHour: number } => {
    let startHour = DAY_START_HOUR;
    let endHour = DAY_END_HOUR;

    // Parse HH:mm format strings
    if (shift.start_time) {
      const [hours, minutes] = shift.start_time.split(':').map(Number);
      startHour = hours + (minutes || 0) / 60;
    }
    if (shift.end_time) {
      const [hours, minutes] = shift.end_time.split(':').map(Number);
      endHour = hours + (minutes || 0) / 60;
    }

    // Clamp to display range
    startHour = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR, startHour));
    endHour = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR, endHour));

    return { startHour, endHour };
  };

  const totalHours = DAY_END_HOUR - DAY_START_HOUR;

  // Get coverage for a specific group from coverageData
  const getCoverageForGroup = (groupId: string) => {
    if (!coverageData?.groups || viewMode !== 'byGroup') return undefined;
    return coverageData.groups.find((g) => g.group_id === groupId);
  };

  // Drag and drop (#2038): the week view in both modes, the day view By Employee only (a
  // By Group day row offers no other day and no employee).
  const dndEnabled = !!onShiftDrop && (calendarView === 'week' || viewMode === 'byEmployee');
  const handleShiftDrop = (shift: Shift, target: ShiftDropTarget) => onShiftDrop?.(shift, target);
  // What a click and a drag do here, under the legend (#2038 UX pass).
  const gridHint = !dndEnabled
    ? i18n._({ id: 'shifts.hint.click', message: 'Click a free area to add a shift. Click a shift to open it.' })
    : viewMode === 'byEmployee' && calendarView === 'day'
      ? i18n._({ id: 'shifts.hint.dragEmployee', message: 'Click a free area to add a shift. Drag a shift to another row to hand it to a colleague.' })
      : viewMode === 'byEmployee'
        ? i18n._({ id: 'shifts.hint.dragDayEmployee', message: 'Click a free cell to add a shift. Drag a shift to another day or row to move it.' })
        : i18n._({ id: 'shifts.hint.dragDay', message: 'Click a free cell to add a shift. Drag a shift to another day to move it.' });
  const renderDragOverlay = (shift: Shift) => (
    <div
      className={cn(
        'rounded-md border px-2 py-1 text-xs shadow-lg pointer-events-none',
        shiftBlockClasses(shift.status)
      )}
    >
      {shift.employee ? `${shift.employee.firstname ?? ''} ${shift.employee.lastname ?? ''}`.trim() : ''}{' '}
      {shift.start_time} - {shift.end_time}
    </div>
  );

  // Availability marks (#2041): By Employee rows of a real employee, when the range is loaded.
  const showAvailability = (resourceId: string) =>
    viewMode === 'byEmployee' && resourceId !== 'unassigned' && !!availability;
  const showsAvailabilityLegend = viewMode === 'byEmployee' && !!availability;

  // Render week view
  if (calendarView === 'week') {
    // Use custom grid template: fixed 176px for resource column (name plus the
    // Zeitkonto badge under it), rest distributed equally
    const gridStyle = {
      display: 'grid',
      gridTemplateColumns: '176px repeat(7, 1fr)',
      gap: '0.5rem',
    };

    return (
      <ShiftDndProvider enabled={dndEnabled} onDrop={handleShiftDrop} renderOverlay={renderDragOverlay} canDrag={canDragShift}>
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header row with day names and dates */}
          <div style={gridStyle} className="mb-2">
            {/* Empty cell for resource names column */}
            <div />

            {/* Day headers */}
            {days.map((date) => (
              <div
                key={date.toISOString()}
                className={cn(
                  'flex flex-col items-center justify-center text-center p-2 border rounded-md min-h-[52px]',
                  isToday(date) && 'bg-primary/10 border-primary/30'
                )}
              >
                <div className="font-semibold text-sm">
                  {formatWeekday(date, i18n.locale, 'short')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {format(date, getShortDayMonthFormat(i18n.locale))}
                </div>
              </div>
            ))}
          </div>

          {/* Resource rows */}
          {resources.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {i18n._({
                id: 'shifts.noResources',
                message: 'No resources found',
              })}
            </div>
          ) : (
            resources.map((resource) => {
              const groupCoverage = getCoverageForGroup(resource.id);
              return (
              <div key={resource.id} style={gridStyle} className="mb-2">
                {/* Resource name cell. The Zeitkonto badge sits under the name, never
                    beside it: beside, it pushed the name out of the 160px column. */}
                <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md overflow-hidden">
                  {viewMode === 'byEmployee' &&
                  resource.id !== 'unassigned' ? (
                    <EmployeeAvatar employee={resource.data as EmployeeData} />
                  ) : viewMode === 'byGroup' &&
                    resource.id !== 'unassigned' ? (
                    <GroupAvatar group={resource.data as GroupData} />
                  ) : (
                    <div className="h-8 w-8 flex items-center justify-center bg-muted rounded-full text-xs font-medium text-muted-foreground">
                      ?
                    </div>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col items-start">
                    <span className="text-sm font-medium truncate max-w-full" title={resource.name}>
                      {resource.name}
                    </span>
                    {viewMode === 'byEmployee' &&
                      resource.id !== 'unassigned' && (
                        <EmployeeZeitkontoBadge
                          projection={zeitkontoByEmployee?.[resource.id]}
                        />
                      )}
                  </div>
                  {viewMode === 'byGroup' && groupCoverage && (
                    <GroupCoverageBadge coverage={groupCoverage} />
                  )}
                </div>

                {/* Day cells */}
                {days.map((date) => {
                  const cellShifts = getShiftsForCell(resource.id, date);
                  const cellAbsences = getAbsencesForCell(resource.id, date);
                  const dayAvailability = showAvailability(resource.id)
                    ? availabilityForDay(availability, resource.id, date)
                    : undefined;
                  return (
                    <ShiftDropCell
                      key={date.toISOString()}
                      resourceId={resource.id}
                      date={date}
                      onClick={() => onCellClick(date, resource.id)}
                      className={cn(
                        // min-w-0 lets a long combination chip truncate instead of widening the column.
                        'min-h-[80px] min-w-0 p-2 border rounded-md transition-colors',
                        'hover:bg-muted/50 cursor-pointer',
                        isToday(date) && 'bg-primary/5 border-primary/20'
                      )}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onCellClick(date, resource.id);
                        }
                      }}
                    >
                      <div className="flex flex-col gap-1">
                        {dayAvailability?.blocked && <BlockedDayLock blocked={dayAvailability.blocked} inline />}
                        {/* Show absences first (they visually block the day) */}
                        {cellAbsences.map((absence) => (
                          <AbsenceCard
                            key={`absence-${absence.id}`}
                            absence={absence}
                            onClick={onAbsenceClick}
                          />
                        ))}
                        {viewMode === 'byGroup' &&
                          combinationsForGroupOn(combinations ?? [], resource.id, date).map(({ combination, role }) => (
                            <CombinationChip
                              key={`combination-${combination.id}`}
                              combination={combination}
                              role={role}
                              onClick={onCombinationClick}
                            />
                          ))}
                        {cellShifts.map((shift) => (
                          <ShiftCard
                            key={shift.id}
                            shift={shift}
                            onClick={() => onShiftClick(shift)}
                            viewMode={viewMode}
                          />
                        ))}
                      </div>
                      {dayAvailability && (
                        <AvailabilityStrip
                          windows={dayAvailability.windows}
                          startHour={DAY_START_HOUR}
                          endHour={DAY_END_HOUR}
                        />
                      )}
                    </ShiftDropCell>
                  );
                })}
              </div>
              );
            })
          )}
        </div>
        <ShiftLegend className="mt-3 px-1" hint={gridHint} />
        {showsAvailabilityLegend && <AvailabilityLegend className="mt-1.5 px-1" />}
      </div>
      </ShiftDndProvider>
    );
  }

  // Render day view with hourly timeline
  return (
    <ShiftDndProvider enabled={dndEnabled} onDrop={handleShiftDrop} renderOverlay={renderDragOverlay} canDrag={canDragShift}>
    <div className="overflow-x-auto">
      <div className="min-w-[1200px]">
        {/* Header row with hours */}
        <div className="flex gap-2 mb-2">
          {/* Empty cell for resource names column */}
          {/* Same fixed width as the resource name cells, so every timeline lines up with the hours */}
          <div className="w-[220px] shrink-0" />

          {/* Hour headers */}
          <div className="flex-1 flex">
            {hours.map((hour) => (
              <div
                key={hour}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center text-center p-2 border-l first:border-l-0 border-t border-b border-r last:rounded-r-md first:rounded-l-md min-h-[52px]',
                  isToday(currentDate) && 'bg-primary/5'
                )}
              >
                <div className="font-semibold text-sm">
                  {hour.toString().padStart(2, '0')}:00
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Resource rows */}
        {resources.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {i18n._({
              id: 'shifts.noResources',
              message: 'No resources found',
            })}
          </div>
        ) : (
          <div className="relative">
          {unitBlocks.map((block) => (
            <CombinationBlock
              key={`block-${block.unit.combination_id}`}
              position={block.position}
              overCapacity={block.unit.over_capacity}
            />
          ))}
          {previewBlock && (
            <CombinationBlock preview position={previewBlock.position} overCapacity={false} />
          )}
          {resources.map((resource) => {
            const groupCoverage = getCoverageForGroup(resource.id);
            const slotGroup =
              viewMode === 'byGroup'
                ? slotCoverage?.groups.find((g) => g.group_id === resource.id)
                : undefined;
            return (
            <div
              key={resource.id}
              className="flex gap-2 mb-2"
              style={viewMode === 'byGroup' ? { height: GROUP_DAY_ROW_HEIGHT } : undefined}
            >
              {/* Resource name cell */}
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md w-[220px] shrink-0 overflow-hidden">
                {viewMode === 'byEmployee' && resource.id !== 'unassigned' ? (
                  <EmployeeAvatar employee={resource.data as EmployeeData} />
                ) : viewMode === 'byGroup' && resource.id !== 'unassigned' ? (
                  <GroupAvatar group={resource.data as GroupData} />
                ) : (
                  <div className="h-8 w-8 flex items-center justify-center bg-muted rounded-full text-xs font-medium text-muted-foreground">
                    ?
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                  <span className="text-sm font-medium truncate max-w-full" title={resource.name}>
                    {resource.name}
                  </span>
                  {slotGroup && (
                    <div className="flex flex-wrap items-center gap-1">
                      <ArrivalsPopover groupName={resource.name} presences={slotGroup.children} />
                      <SurplusRunBadge runs={slotGroup.surplus_runs} />
                    </div>
                  )}
                  {viewMode === 'byEmployee' && resource.id !== 'unassigned' && (
                    <EmployeeZeitkontoBadge
                      projection={zeitkontoByEmployee?.[resource.id]}
                    />
                  )}
                </div>
                {viewMode === 'byGroup' && groupCoverage && (
                  <GroupCoverageBadge coverage={groupCoverage} />
                )}
              </div>

              {/* Timeline area */}
              <ShiftDropCell
                resourceId={resource.id}
                date={currentDate}
                onClick={() => onCellClick(currentDate, resource.id)}
                className={cn(
                  'flex-1 relative border rounded-md transition-colors',
                  // By Group rows have a fixed height (combination blocks span them);
                  // the staffing strip sits under the shift bars.
                  viewMode === 'byGroup' ? 'h-full' : 'min-h-[60px]',
                  'hover:bg-muted/30 cursor-pointer',
                  isToday(currentDate) && 'bg-primary/5 border-primary/20'
                )}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCellClick(currentDate, resource.id);
                  }
                }}
              >
                {/* Hour grid lines */}
                <div className="absolute inset-0 flex pointer-events-none">
                  {hours.map((hour, idx) => (
                    <div
                      key={hour}
                      className={cn(
                        'flex-1 border-l border-border/30',
                        idx === 0 && 'border-l-0'
                      )}
                    />
                  ))}
                </div>

                {/* Availability (#2041): hatched unavailable hours, lock on a blocked day */}
                {showAvailability(resource.id) && (() => {
                  const dayAvailability = availabilityForDay(availability, resource.id, currentDate);
                  return (
                    <>
                      <AvailabilityHatch
                        windows={dayAvailability.windows}
                        startHour={DAY_START_HOUR}
                        endHour={DAY_END_HOUR}
                      />
                      {dayAvailability.blocked && <BlockedDayLock blocked={dayAvailability.blocked} />}
                    </>
                  );
                })()}

                {/* Absences - shown first as background layer */}
                {getAbsencesForResource(resource.id).map((absence) => (
                  <AbsenceBar
                    key={`absence-${absence.id}`}
                    absence={absence}
                    onClick={onAbsenceClick}
                    totalHours={totalHours}
                  />
                ))}

                {/* Shifts */}
                {getShiftsForResource(resource.id).map((shift) => {
                  const { startHour, endHour } = getShiftHours(shift);
                  return (
                    <ShiftBar
                      key={shift.id}
                      shift={shift}
                      onClick={() => onShiftClick(shift)}
                      startHour={startHour}
                      endHour={endHour}
                      totalHours={totalHours}
                      viewMode={viewMode}
                    />
                  );
                })}

                {/* Staffing per 15-minute slot */}
                {slotGroup && slotCoverage && (
                  <GroupStaffingStrip
                    group={slotGroup}
                    axisStart={slotCoverage.axis_start}
                    axisEnd={slotCoverage.axis_end}
                    dayStartHour={DAY_START_HOUR}
                    dayEndHour={DAY_END_HOUR}
                  />
                )}
              </ShiftDropCell>
            </div>
            );
          })}
          {unitBlocks.map((block) => (
            <CombinationBlockLabel
              key={`label-${block.unit.combination_id}`}
              unit={block.unit}
              hostName={block.hostName}
              memberNames={block.memberNames}
              position={block.position}
              onClick={
                onCombinationClick && block.combination
                  ? () => onCombinationClick(block.combination as GroupCombination)
                  : undefined
              }
            />
          ))}
          {previewBlock && (
            <CombinationPreviewLabel
              name={previewBlock.name}
              staffSaved={previewBlock.staffSaved}
              position={previewBlock.position}
            />
          )}
          </div>
        )}
      </div>
      <ShiftLegend className="mt-3 px-1" hint={gridHint} />
      {showsAvailabilityLegend && <AvailabilityLegend className="mt-1.5 px-1" />}
      {viewMode === 'byGroup' && slotCoverage && <StaffingLegend className="mt-1.5 px-1" />}
    </div>
    </ShiftDndProvider>
  );
}
